import React, { useState, useEffect, useCallback } from "react";
import SuperAppButton from "./SuperAppButton";
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaSave,
  FaCopy,
  FaTable,
  FaList,
  FaCode,
} from "react-icons/fa";
import { Modal, Button, Form } from "react-bootstrap";

/**
 * UNIFIED SUPERAPP OBJECT EDITOR
 *
 * Combines sections 5, 6, 7, 9 into one unified editor:
 * - ConfigObj Writer (copy/create)
 * - Field Editor (add/edit/delete fields)
 * - Table/Column Editor (datagrid config)
 * - Raw JSON view
 *
 * This allows creating and editing SuperAppObjects in one place.
 */

// Helper to always hit same-origin API
const api = (path, options = {}) =>
  fetch(`/api${path}`, {
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "same-origin",
    ...options,
  });

export default function UnifiedSuperAppObjectEditor({
  appId,
  onConfigObjCreated,
  /* Vik (Jan 21, 2026) : Added mode prop to support "import" mode (developer only) which shows only bulk import, and "standard" mode which shows the full editor.*/
  mode = "standard",
}) {
  const [activeTab, setActiveTab] = useState("select"); // "select", "fields", "table", "json"
  const [configObjList, setConfigObjList] = useState([]);
  const [selectedConfigObj, setSelectedConfigObj] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ConfigObj Writer state
  const [selectedSourceConfigObj, setSelectedSourceConfigObj] = useState("");
  const [newConfigObjName, setNewConfigObjName] = useState("");

  // Field Editor state
  const [configObjFields, setConfigObjFields] = useState([]);
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  const [fieldForm, setFieldForm] = useState({
    name: "",
    display_name: "",
    input_type: "text",
    placeholder: "",
    required: false,
    readonly: false,
    default: "",
    help: "",
    values: "",
  });

  // Table/Column Editor state
  const [tableColumns, setTableColumns] = useState([]);
  const [editingColumnIndex, setEditingColumnIndex] = useState(null);
  const [columnForm, setColumnForm] = useState({
    selector: "",
    name: "",
    sortable: false,
    minWidth: "25%",
    sortOrder: "",
    hide: "",
  });

  // Raw JSON state
  const [rawJson, setRawJson] = useState("");

  // Bulk Import state
  const [bulkJson, setBulkJson] = useState("");

  const TEMPLATES = {
    users: [
      {
        name: "demo-users",
        displayName: "Users Table",
        tableName: "demo_users",
        collection: "demo_users",
        fields: [
          {
            name: "name",
            display_name: "Full Name",
            input_type: "text",
            required: true,
            placeholder: "Enter full name",
          },
          {
            name: "email",
            display_name: "Email Address",
            input_type: "text",
            required: true,
            placeholder: "user@example.com",
          },
          {
            name: "role",
            display_name: "Role",
            input_type: "text",
            required: false,
            placeholder: "e.g., Admin, User",
          },
          {
            name: "status",
            display_name: "Status",
            input_type: "text",
            required: false,
            placeholder: "Active, Inactive",
          },
        ],
        table: [
          {
            selector: "name",
            name: "Name",
            sortable: true,
            minWidth: "200px",
          },
          {
            selector: "email",
            name: "Email",
            sortable: true,
            minWidth: "250px",
          },
          {
            selector: "role",
            name: "Role",
            sortable: true,
            minWidth: "120px",
          },
          {
            selector: "status",
            name: "Status",
            sortable: true,
            minWidth: "100px",
          },
        ],
        security: {
          view: "AllUsers",
          create: "AllUsers",
          edit: "AllUsers",
          delete: "CreatedBy",
        },
      },
    ],
    products: [
      {
        name: "demo-products",
        displayName: "Products Table",
        tableName: "demo_products",
        collection: "demo_products",
        fields: [
          {
            name: "title",
            display_name: "Product Name",
            input_type: "text",
            required: true,
            placeholder: "Enter product name",
          },
          {
            name: "category",
            display_name: "Category",
            input_type: "text",
            required: false,
            placeholder: "e.g., Electronics, Clothing",
          },
          {
            name: "price",
            display_name: "Price",
            input_type: "text",
            required: false,
            placeholder: "0.00",
          },
          {
            name: "stock",
            display_name: "Stock Quantity",
            input_type: "text",
            required: false,
            placeholder: "0",
          },
        ],
        table: [
          {
            selector: "title",
            name: "Product Name",
            sortable: true,
            minWidth: "200px",
          },
          {
            selector: "category",
            name: "Category",
            sortable: true,
            minWidth: "150px",
          },
          {
            selector: "price",
            name: "Price",
            sortable: true,
            minWidth: "100px",
          },
          {
            selector: "stock",
            name: "In Stock",
            sortable: true,
            minWidth: "100px",
          },
        ],
        security: {
          view: "AllUsers",
          create: "AllUsers",
          edit: "AllUsers",
          delete: "CreatedBy",
        },
      },
    ],
    tasks: [
      {
        name: "demo-tasks",
        displayName: "Tasks Table",
        tableName: "demo_tasks",
        collection: "demo_tasks",
        fields: [
          {
            name: "task",
            display_name: "Task Description",
            input_type: "text",
            required: true,
            placeholder: "Enter task description",
          },
          {
            name: "priority",
            display_name: "Priority",
            input_type: "text",
            required: false,
            placeholder: "High, Medium, Low",
          },
          {
            name: "status",
            display_name: "Status",
            input_type: "text",
            required: false,
            placeholder: "Todo, In Progress, Done",
          },
          {
            name: "dueDate",
            display_name: "Due Date",
            input_type: "text",
            required: false,
            placeholder: "YYYY-MM-DD",
          },
        ],
        table: [
          {
            selector: "task",
            name: "Task",
            sortable: true,
            minWidth: "250px",
          },
          {
            selector: "priority",
            name: "Priority",
            sortable: true,
            minWidth: "120px",
          },
          {
            selector: "status",
            name: "Status",
            sortable: true,
            minWidth: "120px",
          },
          {
            selector: "dueDate",
            name: "Due Date",
            sortable: true,
            minWidth: "150px",
          },
        ],
        security: {
          view: "AllUsers",
          create: "AllUsers",
          edit: "AllUsers",
          delete: "CreatedBy",
        },
      },
    ],
  };

  const handleLoadTemplate = (templateKey) => {
    const template = TEMPLATES[templateKey];
    if (template) {
      setBulkJson(JSON.stringify(template, null, 2));
    }
  };

  const handleBulkImport = async () => {
    if (!bulkJson.trim()) return;

    try {
      setSaving(true);
      let objects;
      let inputToParse = bulkJson.trim();

      // Remove trailing semicolon if present (common when copying from JS files)
      if (inputToParse.endsWith(";")) {
        inputToParse = inputToParse.slice(0, -1).trim();
      }

      // Try parsing as JSON first
      try {
        objects = JSON.parse(inputToParse);
      } catch (e) {
        // If JSON fails, try evaluating as JavaScript object (to support functions, comments, etc.)
        try {
          // eslint-disable-next-line no-eval
          const evaluated = eval(`(${inputToParse})`);
          objects = evaluated;
        } catch (evalError) {
          console.error("Eval error:", evalError);
          throw new Error(
            `Invalid format: ${evalError.message}. Must be valid JSON or a JavaScript object/array.`,
          );
        }
      }

      // Handle single object vs array
      if (!Array.isArray(objects)) {
        objects = [objects];
      }

      // Helper to stringify functions for storage
      const stringifyFunctions = (obj) => {
        if (obj === null || obj === undefined) return obj;
        if (typeof obj === "function") {
          return { _functionString: obj.toString() };
        }
        if (Array.isArray(obj)) {
          return obj.map(stringifyFunctions);
        }
        if (typeof obj === "object") {
          // Skip if already stringified
          if (obj._functionString && Object.keys(obj).length === 1) return obj;

          const result = {};
          for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
              result[key] = stringifyFunctions(obj[key]);
            }
          }
          return result;
        }
        return obj;
      };

      // Process objects to stringify functions
      objects = stringifyFunctions(objects);

      // Load current SuperApp config
      const currentAppId =
        appId ||
        new URLSearchParams(window.location.search).get("appId") ||
        "Ben-test";
      const superAppResponse = await api(
        `/superapp/config/${encodeURIComponent(currentAppId)}`,
      );

      if (!superAppResponse.ok) {
        throw new Error(`Failed to load config: ${superAppResponse.status}`);
      }

      const superAppData = await superAppResponse.json();
      const superAppConfig = superAppData?.config || superAppData;

      if (!superAppConfig.app_config) superAppConfig.app_config = {};

      let importedCount = 0;
      for (const obj of objects) {
        if (!obj.name) {
          console.warn("Skipping object without name:", obj);
          continue;
        }
        // Ensure required fields
        const newObj = {
          ...obj,
          tableName: obj.tableName || obj.name.toLowerCase(),
          collection: obj.collection || obj.name.toLowerCase(),
          fields: obj.fields || [],
          table: obj.table || [],
          security: obj.security || {
            view: "AllUsers",
            edit: "AllUsers",
            create: "AllUsers",
            delete: "AllUsers",
          },
        };
        superAppConfig.app_config[obj.name] = newObj;
        importedCount++;
      }

      // Save back to SuperApp config
      const saveResponse = await api(
        `/superapp/config/${encodeURIComponent(currentAppId)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ config: superAppConfig }),
        },
      );

      if (!saveResponse.ok) {
        throw new Error(`Failed to save: ${saveResponse.status}`);
      }

      if (saveResponse.ok) {
        alert(`Successfully imported ${importedCount} objects!`);
        setBulkJson("");
        await loadConfigObjList();
        // Select the first imported object if available
        if (objects.length > 0 && objects[0].name) {
          setSelectedConfigObj(objects[0].name);
        }
      }
    } catch (e) {
      console.error("Failed to import objects:", e);
      alert(`Import Error: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Load configObj list on mount
  useEffect(() => {
    loadConfigObjList();
  }, []);

  // Load fields/table when configObj changes
  useEffect(() => {
    if (selectedConfigObj) {
      loadConfigObjFields(selectedConfigObj);
      loadTableColumns(selectedConfigObj);
      loadRawJson(selectedConfigObj);
    } else {
      setConfigObjFields([]);
      setTableColumns([]);
      setRawJson("");
    }
  }, [selectedConfigObj]);

  /**
   * Helper: Get a ConfigObj from the right place (app_config first, then fallbacks)
   */
  const getConfigObj = async (configObjName) => {
    if (!configObjName) return null;

    // First, try to get from main SuperApp config's app_config
    if (appId) {
      try {
        const superAppResponse = await api(
          `/superapp/config/${encodeURIComponent(appId)}`,
        );
        if (superAppResponse.ok) {
          const superAppData = await superAppResponse.json();
          const superAppConfig = superAppData?.config || superAppData;
          if (superAppConfig?.app_config?.[configObjName]) {
            return superAppConfig.app_config[configObjName];
          }
        }
      } catch (e) {
        console.warn("Failed to load from app_config, trying fallback:", e);
      }
    }

    // Fall back to old config collection
    try {
      const response = await api("/getObjects/config");
      const text = await response.text();

      if (
        text.trim().startsWith("<!DOCTYPE") ||
        text.trim().startsWith("<html")
      ) {
        // Backend returned HTML, try localStorage
        const cached = localStorage.getItem("demoConfigObjs");
        if (cached) {
          const demoConfigObjs = JSON.parse(cached);
          return demoConfigObjs[configObjName] || null;
        }
        return null;
      }

      const configArray = JSON.parse(text);
      const fullConfig =
        Array.isArray(configArray) && configArray.length > 0
          ? configArray[0]
          : {};
      if (fullConfig.app_config?.[configObjName]) {
        return fullConfig.app_config[configObjName];
      }
    } catch (e) {
      console.warn("Failed to load from old config collection:", e);
    }

    // Final fallback: localStorage
    try {
      const cached = localStorage.getItem("demoConfigObjs");
      if (cached) {
        const demoConfigObjs = JSON.parse(cached);
        return demoConfigObjs[configObjName] || null;
      }
    } catch (e) {
      console.error("Failed to parse cached configObjs:", e);
    }

    return null;
  };

  /**
   * Load list of available configObjs from Firestore (with localStorage fallback)
   *
   * Priority:
   * 1. Try to get from main SuperApp config's app_config (superapp_configs collection)
   * 2. Fall back to old config collection (/api/getObjects/config)
   * 3. Fall back to localStorage
   */
  const loadConfigObjList = async () => {
    try {
      setLoading(true);

      // First, try to get from main SuperApp config's app_config
      if (appId) {
        try {
          const superAppResponse = await api(
            `/superapp/config/${encodeURIComponent(appId)}`,
          );
          if (superAppResponse.ok) {
            const superAppData = await superAppResponse.json();
            const superAppConfig = superAppData?.config || superAppData;
            if (
              superAppConfig?.app_config &&
              typeof superAppConfig.app_config === "object"
            ) {
              const configObjNames = Object.keys(superAppConfig.app_config);
              if (configObjNames.length > 0) {
                console.log(
                  `✅ Loaded ${configObjNames.length} ConfigObjs from app_config:`,
                  configObjNames,
                );
                setConfigObjList(configObjNames);
                return;
              }
            }
          }
        } catch (e) {
          console.warn("Failed to load from app_config, trying fallback:", e);
        }
      }

      // Fall back to old config collection
      const response = await api("/getObjects/config");
      const text = await response.text();

      // Check if response is HTML (backend error or marketing site)
      if (
        text.trim().startsWith("<!DOCTYPE") ||
        text.trim().startsWith("<html")
      ) {
        console.warn(
          "Backend returned HTML instead of JSON, using localStorage fallback",
        );
        // Fall back to localStorage
        try {
          const cached = localStorage.getItem("demoConfigObjs");
          if (cached) {
            const demoConfigObjs = JSON.parse(cached);
            const configObjNames = Object.keys(demoConfigObjs);
            setConfigObjList(configObjNames);
            return;
          }
        } catch (e) {
          console.error("Failed to parse cached configObjs:", e);
        }
        setConfigObjList([]);
        return;
      }

      const configArray = JSON.parse(text);

      if (Array.isArray(configArray) && configArray.length > 0) {
        const fullConfig = configArray[0];
        const configObjNames = fullConfig.app_config
          ? Object.keys(fullConfig.app_config)
          : [];
        setConfigObjList(configObjNames);
      } else {
        setConfigObjList([]);
      }
    } catch (e) {
      console.error("Failed to load configObj list:", e);
      // Try localStorage fallback
      try {
        const cached = localStorage.getItem("demoConfigObjs");
        if (cached) {
          const demoConfigObjs = JSON.parse(cached);
          const configObjNames = Object.keys(demoConfigObjs);
          setConfigObjList(configObjNames);
          return;
        }
      } catch (e2) {
        console.error("Failed to parse cached configObjs:", e2);
      }
      setConfigObjList([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Copy a configObj to create a new one
   */
  const copyConfigObj = async () => {
    if (!selectedSourceConfigObj || !newConfigObjName) {
      alert("Please select a source configObj and enter a new name");
      return;
    }

    if (newConfigObjName === selectedSourceConfigObj) {
      alert("New name must be different from source name");
      return;
    }

    try {
      setSaving(true);

      // Determine source config based on appId
      let fullConfig = {};
      let sourceConfigObj = null;

      // 1. Try SuperApp config first
      const currentAppId =
        appId ||
        new URLSearchParams(window.location.search).get("appId") ||
        "Ben-test";
      try {
        const superAppResponse = await api(
          `/superapp/config/${encodeURIComponent(currentAppId)}`,
        );
        if (superAppResponse.ok) {
          const superAppData = await superAppResponse.json();
          const superAppConfig = superAppData?.config || superAppData;
          if (superAppConfig?.app_config?.[selectedSourceConfigObj]) {
            fullConfig = superAppConfig;
            sourceConfigObj = JSON.parse(
              JSON.stringify(
                superAppConfig.app_config[selectedSourceConfigObj],
              ),
            );
          }
        }
      } catch (e) {
        console.warn("Failed to load from superapp config for copy:", e);
      }

      // 2. Fallback to legacy config if not found
      if (!sourceConfigObj) {
        const response = await api("/getObjects/config");
        const configArray = await response.json();
        if (Array.isArray(configArray) && configArray.length > 0) {
          const legacyConfig = configArray[0];
          if (legacyConfig.app_config?.[selectedSourceConfigObj]) {
            // We need to be careful here: if we are in a SuperApp context, we should probably save to SuperApp config
            // but if we found it in legacy, we might want to copy it TO SuperApp config.
            sourceConfigObj = JSON.parse(
              JSON.stringify(legacyConfig.app_config[selectedSourceConfigObj]),
            );

            // If we didn't load fullConfig from SuperApp, we need to load it now to save into it
            if (!fullConfig.app_config) {
              // Try to get SuperApp config again or init empty
              try {
                const saRes = await api(
                  `/superapp/config/${encodeURIComponent(currentAppId)}`,
                );
                if (saRes.ok) {
                  const saData = await saRes.json();
                  fullConfig = saData?.config || saData;
                } else {
                  fullConfig = { app_config: {} };
                }
              } catch (e) {
                fullConfig = { app_config: {} };
              }
            }
          }
        }
      }

      if (!sourceConfigObj) {
        throw new Error(
          `Source configObj "${selectedSourceConfigObj}" not found`,
        );
      }

      if (sourceConfigObj.name) sourceConfigObj.name = newConfigObjName;
      if (sourceConfigObj.tableName)
        sourceConfigObj.tableName = newConfigObjName.toLowerCase();
      if (sourceConfigObj.collection)
        sourceConfigObj.collection = newConfigObjName.toLowerCase();

      if (!fullConfig.app_config) fullConfig.app_config = {};
      fullConfig.app_config[newConfigObjName] = sourceConfigObj;

      // Save back to SuperApp config
      const saveResponse = await api(
        `/superapp/config/${encodeURIComponent(currentAppId)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ config: fullConfig }),
        },
      );

      const saveData = await saveResponse.json();
      if (saveResponse.ok) {
        alert(`Successfully created "${newConfigObjName}"`);
        setNewConfigObjName("");
        setSelectedSourceConfigObj("");
        await loadConfigObjList();
        setSelectedConfigObj(newConfigObjName);
        if (onConfigObjCreated) onConfigObjCreated(newConfigObjName);
      } else {
        throw new Error(saveData.error || "Failed to save configObj");
      }
    } catch (e) {
      console.error("Failed to copy configObj:", e);
      alert(`Error: ${e.message || "Failed to copy configObj"}`);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Create a new empty configObj (with localStorage fallback)
   */
  const createNewConfigObj = async () => {
    const name = prompt("Enter name for new SuperAppObject:");
    if (!name || !name.trim()) return;

    const newName = name.trim();
    if (configObjList.includes(newName)) {
      alert(`ConfigObj "${newName}" already exists`);
      return;
    }

    try {
      setSaving(true);

      try {
        setSaving(true);

        // Determine source config based on appId
        let fullConfig = {};
        const currentAppId =
          appId ||
          new URLSearchParams(window.location.search).get("appId") ||
          "Ben-test";

        // 1. Try SuperApp config first
        try {
          const superAppResponse = await api(
            `/superapp/config/${encodeURIComponent(currentAppId)}`,
          );
          if (superAppResponse.ok) {
            const superAppData = await superAppResponse.json();
            fullConfig = superAppData?.config || superAppData;
          } else {
            // If not found, init empty
            fullConfig = { app_config: {} };
          }
        } catch (e) {
          console.warn("Failed to load from superapp config for create:", e);
          fullConfig = { app_config: {} };
        }

        if (!fullConfig.app_config) fullConfig.app_config = {};

        const newConfigObj = {
          name: newName,
          displayType: "TABLE",
          tableName: newName.toLowerCase(),
          collection: newName.toLowerCase(),
          fields: [],
          table: [],
          security: {
            view: "AllUsers",
            edit: "AllUsers",
            create: "AllUsers",
            delete: "AllUsers",
          },
        };

        fullConfig.app_config[newName] = newConfigObj;

        // Try to save to backend (SuperApp config)
        try {
          const saveResponse = await api(
            `/superapp/config/${encodeURIComponent(currentAppId)}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ config: fullConfig }),
            },
          );

          const saveText = await saveResponse.text();
          if (
            saveText.trim().startsWith("<!DOCTYPE") ||
            saveText.trim().startsWith("<html")
          ) {
            throw new Error("Backend unavailable, saving to localStorage");
          }

          const saveData = JSON.parse(saveText);
          if (saveResponse.ok) {
            alert(`Successfully created "${newName}"`);
            await loadConfigObjList();
            setSelectedConfigObj(newName);
            if (onConfigObjCreated) onConfigObjCreated(newName);
            return;
          } else {
            throw new Error(saveData.error || "Failed to save configObj");
          }
        } catch (saveError) {
          // Fallback to localStorage
          console.warn("Backend save failed, using localStorage:", saveError);
          const demoConfigObjs = fullConfig.app_config || {};
          localStorage.setItem(
            "demoConfigObjs",
            JSON.stringify(demoConfigObjs),
          );
          alert(`Created "${newName}" (saved locally - backend unavailable)`);
          await loadConfigObjList();
          setSelectedConfigObj(newName);
          if (onConfigObjCreated) onConfigObjCreated(newName);
        }
      } catch (e) {
        console.error("Failed to create configObj:", e);
        alert(`Error: ${e.message || "Failed to create configObj"}`);
      } finally {
        setSaving(false);
      }
    } catch (e) {
      console.error("Failed to create configObj:", e);
      alert(`Error: ${e.message || "Failed to create configObj"}`);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Load fields for selected configObj (with localStorage fallback)
   */
  const loadConfigObjFields = async (configObjName) => {
    if (!configObjName) {
      setConfigObjFields([]);
      return;
    }

    try {
      const configObj = await getConfigObj(configObjName);
      if (configObj && Array.isArray(configObj.fields)) {
        setConfigObjFields(configObj.fields);
      } else {
        setConfigObjFields([]);
      }
    } catch (e) {
      console.error("Failed to load configObj fields:", e);
      setConfigObjFields([]);
    }
  };

  /**
   * Load table columns for selected configObj (with localStorage fallback)
   */
  const loadTableColumns = async (configObjName) => {
    if (!configObjName) {
      setTableColumns([]);
      return;
    }

    try {
      const configObj = await getConfigObj(configObjName);
      if (configObj && Array.isArray(configObj.table)) {
        const columns = configObj.table;
        const columnsForEditing = columns.map((col) => {
          const colCopy = { ...col };
          // Use _functionString instead of __function__ (Firestore reserves __ prefix)
          if (
            colCopy.selector &&
            typeof colCopy.selector === "object" &&
            colCopy.selector._functionString
          ) {
            const match = colCopy.selector._functionString.match(
              /row\[?['"](.*?)['"]\]?/,
            );
            colCopy.selector = match ? match[1] : "";
          } else if (typeof colCopy.selector === "function") {
            const match = colCopy.selector
              .toString()
              .match(/row\[?['"](.*?)['"]\]?/);
            colCopy.selector = match ? match[1] : "";
          }
          return colCopy;
        });
        setTableColumns(columnsForEditing);
      } else {
        setTableColumns([]);
      }
    } catch (e) {
      console.error("Failed to load table columns:", e);
      setTableColumns([]);
    }
  };

  /**
   * Load raw JSON for selected configObj (with localStorage fallback)
   */
  const loadRawJson = async (configObjName) => {
    if (!configObjName) {
      setRawJson("");
      return;
    }

    try {
      const configObj = await getConfigObj(configObjName);
      if (configObj) {
        setRawJson(JSON.stringify(configObj, null, 2));
      } else {
        setRawJson("");
      }
    } catch (e) {
      console.error("Failed to load raw JSON:", e);
      setRawJson("");
    }
  };

  /**
   * Save fields back to configObj
   * Saves to SuperApp config's app_config (superapp_configs collection)
   */
  const saveFields = async () => {
    if (!selectedConfigObj) {
      alert("Please select a configObj first");
      return;
    }

    try {
      setSaving(true);

      // Load current SuperApp config
      const currentAppId =
        appId ||
        new URLSearchParams(window.location.search).get("appId") ||
        "Ben-test";
      const superAppResponse = await api(
        `/superapp/config/${encodeURIComponent(currentAppId)}`,
      );

      if (!superAppResponse.ok) {
        const text = await superAppResponse.text();
        if (
          text.trim().startsWith("<!DOCTYPE") ||
          text.trim().startsWith("<html")
        ) {
          throw new Error(
            "Backend returned HTML instead of JSON. Is the server running?",
          );
        }
        throw new Error(`Failed to load config: ${superAppResponse.status}`);
      }

      const superAppData = await superAppResponse.json();
      const superAppConfig = superAppData?.config || superAppData;

      if (
        !superAppConfig.app_config ||
        !superAppConfig.app_config[selectedConfigObj]
      ) {
        throw new Error(
          `ConfigObj "${selectedConfigObj}" not found in app_config`,
        );
      }

      // Update the fields
      superAppConfig.app_config[selectedConfigObj].fields = configObjFields;

      // Save back to SuperApp config
      const saveResponse = await api(
        `/superapp/config/${encodeURIComponent(currentAppId)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ config: superAppConfig }),
        },
      );

      if (!saveResponse.ok) {
        const text = await saveResponse.text();
        if (
          text.trim().startsWith("<!DOCTYPE") ||
          text.trim().startsWith("<html")
        ) {
          throw new Error(
            "Backend returned HTML instead of JSON. Is the server running?",
          );
        }
        const errorData = await saveResponse
          .json()
          .catch(() => ({ error: text.substring(0, 200) }));
        throw new Error(
          errorData.error || `Failed to save: ${saveResponse.status}`,
        );
      }

      const saveData = await saveResponse.json();
      alert(`Successfully saved fields for "${selectedConfigObj}"`);
      setEditingFieldIndex(null);
      resetFieldForm();

      // Reload the fields to reflect the saved state
      await loadConfigObjFields(selectedConfigObj);
    } catch (e) {
      console.error("Failed to save fields:", e);
      alert(`Error: ${e.message || "Failed to save fields"}`);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Save table columns back to configObj
   * Saves to SuperApp config's app_config (superapp_configs collection)
   */
  const saveTableColumns = async () => {
    if (!selectedConfigObj) {
      alert("Please select a configObj first");
      return;
    }

    try {
      setSaving(true);

      // Load current SuperApp config
      const currentAppId =
        appId ||
        new URLSearchParams(window.location.search).get("appId") ||
        "Ben-test";
      const superAppResponse = await api(
        `/superapp/config/${encodeURIComponent(currentAppId)}`,
      );

      if (!superAppResponse.ok) {
        const text = await superAppResponse.text();
        if (
          text.trim().startsWith("<!DOCTYPE") ||
          text.trim().startsWith("<html")
        ) {
          throw new Error(
            "Backend returned HTML instead of JSON. Is the server running?",
          );
        }
        throw new Error(`Failed to load config: ${superAppResponse.status}`);
      }

      const superAppData = await superAppResponse.json();
      const superAppConfig = superAppData?.config || superAppData;

      if (
        !superAppConfig.app_config ||
        !superAppConfig.app_config[selectedConfigObj]
      ) {
        throw new Error(
          `ConfigObj "${selectedConfigObj}" not found in app_config`,
        );
      }

      const columnsWithFunctions = tableColumns.map((col) => {
        const colCopy = { ...col };
        if (typeof colCopy.selector === "string") {
          const selectorStr = colCopy.selector.trim();
          // Check if it looks like a function definition
          if (
            selectorStr.includes("=>") ||
            selectorStr.startsWith("function")
          ) {
            colCopy.selector = { _functionString: selectorStr };
          } else {
            // Treat as field name
            colCopy.selector = {
              _functionString: `(row) => row['${selectorStr}']`,
            };
          }
        } else if (typeof colCopy.selector === "function") {
          colCopy.selector = { _functionString: colCopy.selector.toString() };
        }
        return colCopy;
      });

      superAppConfig.app_config[selectedConfigObj].table = columnsWithFunctions;

      // Save back to SuperApp config
      const saveResponse = await api(
        `/superapp/config/${encodeURIComponent(currentAppId)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ config: superAppConfig }),
        },
      );

      if (!saveResponse.ok) {
        const text = await saveResponse.text();
        if (
          text.trim().startsWith("<!DOCTYPE") ||
          text.trim().startsWith("<html")
        ) {
          throw new Error(
            "Backend returned HTML instead of JSON. Is the server running?",
          );
        }
        const errorData = await saveResponse
          .json()
          .catch(() => ({ error: text.substring(0, 200) }));
        throw new Error(
          errorData.error || `Failed to save: ${saveResponse.status}`,
        );
      }

      const saveData = await saveResponse.json();
      alert(`Successfully saved table columns for "${selectedConfigObj}"`);
      setEditingColumnIndex(null);
      resetColumnForm();

      // Reload the columns to reflect the saved state
      await loadTableColumns(selectedConfigObj);
    } catch (e) {
      console.error("Failed to save table columns:", e);
      alert(`Error: ${e.message || "Failed to save table columns"}`);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Save raw JSON back to configObj
   * Saves to SuperApp config's app_config (superapp_configs collection)
   */
  const saveRawJson = async () => {
    if (!selectedConfigObj) {
      alert("Please select a configObj first");
      return;
    }

    try {
      setSaving(true);
      const parsed = JSON.parse(rawJson);

      // Load current SuperApp config
      const currentAppId =
        appId ||
        new URLSearchParams(window.location.search).get("appId") ||
        "Ben-test";
      const superAppResponse = await api(
        `/superapp/config/${encodeURIComponent(currentAppId)}`,
      );

      if (!superAppResponse.ok) {
        const text = await superAppResponse.text();
        if (
          text.trim().startsWith("<!DOCTYPE") ||
          text.trim().startsWith("<html")
        ) {
          throw new Error(
            "Backend returned HTML instead of JSON. Is the server running?",
          );
        }
        throw new Error(`Failed to load config: ${superAppResponse.status}`);
      }

      const superAppData = await superAppResponse.json();
      const superAppConfig = superAppData?.config || superAppData;

      if (!superAppConfig.app_config) superAppConfig.app_config = {};
      superAppConfig.app_config[selectedConfigObj] = parsed;

      // Save back to SuperApp config
      const saveResponse = await api(
        `/superapp/config/${encodeURIComponent(currentAppId)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ config: superAppConfig }),
        },
      );

      if (!saveResponse.ok) {
        const text = await saveResponse.text();
        if (
          text.trim().startsWith("<!DOCTYPE") ||
          text.trim().startsWith("<html")
        ) {
          throw new Error(
            "Backend returned HTML instead of JSON. Is the server running?",
          );
        }
        const errorData = await saveResponse
          .json()
          .catch(() => ({ error: text.substring(0, 200) }));
        throw new Error(
          errorData.error || `Failed to save: ${saveResponse.status}`,
        );
      }

      const saveData = await saveResponse.json();
      alert(`Successfully saved "${selectedConfigObj}"`);
      await loadConfigObjFields(selectedConfigObj);
      await loadTableColumns(selectedConfigObj);
      await loadRawJson(selectedConfigObj);
    } catch (e) {
      console.error("Failed to save raw JSON:", e);
      if (e instanceof SyntaxError) {
        alert(`Error: Invalid JSON - ${e.message}`);
      } else {
        alert(`Error: ${e.message || "Save failed"}`);
      }
    } finally {
      setSaving(false);
    }
  };

  // Field editor functions
  const addField = () => {
    if (!fieldForm.name || !fieldForm.display_name) {
      alert("Name and Display Name are required");
      return;
    }
    if (configObjFields.some((f) => f.name === fieldForm.name)) {
      alert(`Field with name "${fieldForm.name}" already exists`);
      return;
    }
    const newField = {
      name: fieldForm.name,
      display_name: fieldForm.display_name,
      input_type: fieldForm.input_type,
      placeholder: fieldForm.placeholder || "",
      required: fieldForm.required || false,
      readonly: fieldForm.readonly || false,
      default: fieldForm.default || "",
      help: fieldForm.help || "",
    };
    if (fieldForm.input_type === "dropdown" && fieldForm.values) {
      newField.values = fieldForm.values
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v);
    }
    setConfigObjFields([...configObjFields, newField]);
    resetFieldForm();
  };

  const startEditField = (index) => {
    const field = configObjFields[index];
    setFieldForm({
      name: field.name || "",
      display_name: field.display_name || "",
      input_type: field.input_type || "text",
      placeholder: field.placeholder || "",
      required: field.required || false,
      readonly: field.readonly || false,
      default: field.default || "",
      help: field.help || "",
      values: Array.isArray(field.values) ? field.values.join(", ") : "",
    });
    setEditingFieldIndex(index);
  };

  const updateField = () => {
    if (editingFieldIndex === null) return;
    if (!fieldForm.name || !fieldForm.display_name) {
      alert("Name and Display Name are required");
      return;
    }
    const updatedFields = [...configObjFields];
    const updatedField = {
      ...updatedFields[editingFieldIndex],
      name: fieldForm.name,
      display_name: fieldForm.display_name,
      input_type: fieldForm.input_type,
      placeholder: fieldForm.placeholder || "",
      required: fieldForm.required || false,
      readonly: fieldForm.readonly || false,
      default: fieldForm.default || "",
      help: fieldForm.help || "",
    };
    if (fieldForm.input_type === "dropdown" && fieldForm.values) {
      updatedField.values = fieldForm.values
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v);
    }
    updatedFields[editingFieldIndex] = updatedField;
    setConfigObjFields(updatedFields);
    setEditingFieldIndex(null);
    resetFieldForm();
  };

  const deleteField = (index) => {
    if (
      !window.confirm(`Delete field "${configObjFields[index].display_name}"?`)
    )
      return;
    const updatedFields = configObjFields.filter((_, i) => i !== index);
    setConfigObjFields(updatedFields);
    if (editingFieldIndex === index) {
      setEditingFieldIndex(null);
      resetFieldForm();
    }
  };

  const resetFieldForm = () => {
    setFieldForm({
      name: "",
      display_name: "",
      input_type: "text",
      placeholder: "",
      required: false,
      readonly: false,
      default: "",
      help: "",
      values: "",
    });
  };

  // Table column editor functions
  const addColumn = () => {
    if (!columnForm.selector || !columnForm.name) {
      alert("Selector and Name are required");
      return;
    }
    const newColumn = {
      selector: columnForm.selector,
      name: columnForm.name,
      sortable: columnForm.sortable || false,
      minWidth: columnForm.minWidth || "25%",
    };
    if (columnForm.sortOrder) newColumn.sortOrder = columnForm.sortOrder;
    if (columnForm.hide) newColumn.hide = columnForm.hide;
    setTableColumns([...tableColumns, newColumn]);
    resetColumnForm();
  };

  const startEditColumn = (index) => {
    const column = tableColumns[index];
    const selectorStr =
      typeof column.selector === "function"
        ? column.selector.toString().match(/row\[?['"](.*?)['"]\]?/)?.[1] || ""
        : column.selector || "";
    setColumnForm({
      selector: selectorStr,
      name: column.name || "",
      sortable: column.sortable || false,
      minWidth: column.minWidth || "25%",
      sortOrder: column.sortOrder || "",
      hide: column.hide || "",
    });
    setEditingColumnIndex(index);
  };

  const updateColumn = () => {
    if (editingColumnIndex === null) return;
    if (!columnForm.selector || !columnForm.name) {
      alert("Selector and Name are required");
      return;
    }
    const updatedColumns = [...tableColumns];
    const updatedColumn = {
      ...updatedColumns[editingColumnIndex],
      selector: columnForm.selector,
      name: columnForm.name,
      sortable: columnForm.sortable || false,
      minWidth: columnForm.minWidth || "25%",
    };
    if (columnForm.sortOrder) updatedColumn.sortOrder = columnForm.sortOrder;
    else delete updatedColumn.sortOrder;
    if (columnForm.hide) updatedColumn.hide = columnForm.hide;
    else delete updatedColumn.hide;
    updatedColumns[editingColumnIndex] = updatedColumn;
    setTableColumns(updatedColumns);
    setEditingColumnIndex(null);
    resetColumnForm();
  };

  const deleteColumn = (index) => {
    if (!window.confirm(`Delete column "${tableColumns[index].name}"?`)) return;
    const updatedColumns = tableColumns.filter((_, i) => i !== index);
    setTableColumns(updatedColumns);
    if (editingColumnIndex === index) {
      setEditingColumnIndex(null);
      resetColumnForm();
    }
  };

  const resetColumnForm = () => {
    setColumnForm({
      selector: "",
      name: "",
      sortable: false,
      minWidth: "25%",
      sortOrder: "",
      hide: "",
    });
  };

  const deleteConfigObj = async () => {
    if (!selectedConfigObj) return;
    if (
      !window.confirm(
        `Are you sure you want to delete "${selectedConfigObj}"? This cannot be undone.`,
      )
    )
      return;

    try {
      setSaving(true);
      const currentAppId =
        appId ||
        new URLSearchParams(window.location.search).get("appId") ||
        "Ben-test";

      // Fetch current config
      const superAppResponse = await api(
        `/superapp/config/${encodeURIComponent(currentAppId)}`,
      );
      if (!superAppResponse.ok) throw new Error("Failed to load config");

      const superAppData = await superAppResponse.json();
      const superAppConfig = superAppData?.config || superAppData;

      if (
        superAppConfig.app_config &&
        superAppConfig.app_config[selectedConfigObj]
      ) {
        delete superAppConfig.app_config[selectedConfigObj];

        // Save back
        const saveResponse = await api(
          `/superapp/config/${encodeURIComponent(currentAppId)}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ config: superAppConfig }),
          },
        );

        if (saveResponse.ok) {
          alert(`Successfully deleted "${selectedConfigObj}"`);
          setSelectedConfigObj("");
          await loadConfigObjList();
        } else {
          throw new Error("Failed to save config after deletion");
        }
      }
    } catch (e) {
      console.error("Failed to delete object:", e);
      alert(`Error: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="unified-editor-wrapper">
      {/* Tab Navigation */}
      {/* Vik (Jan 21, 2026) : Hide standard tabs in "import" mode to keep the UI focused on bulk import for developers. */}
      {mode === "standard" && (
        <div className="editor-tabs">
          <button
            onClick={() => setActiveTab("select")}
            className={`editor-tab ${activeTab === "select" ? "active" : ""}`}
          >
            <FaCopy className="tab-icon" /> Select/Create
          </button>
          {selectedConfigObj && (
            <>
              <button
                onClick={() => setActiveTab("fields")}
                className={`editor-tab ${activeTab === "fields" ? "active" : ""}`}
              >
                <FaList className="tab-icon" /> Fields
              </button>
              <button
                onClick={() => setActiveTab("table")}
                className={`editor-tab ${activeTab === "table" ? "active" : ""}`}
              >
                <FaTable className="tab-icon" /> Table/Columns
              </button>
              <button
                onClick={() => setActiveTab("json")}
                className={`editor-tab ${activeTab === "json" ? "active" : ""}`}
              >
                <FaCode className="tab-icon" /> Raw JSON
              </button>
            </>
          )}
        </div>
      )}

      {/* Tab Content */}
      <div className="tab-content-area">
        {activeTab === "select" && (
          <div className="animate-fade-in">
            <div className="section-header">
              <h3 className="section-title">Select or Create SuperAppObject</h3>
            </div>

            {/* Vik (Jan 21, 2026) : Hide standard selection/creation tools in "import" mode. */}
            {mode === "standard" && (
              <>
                <div className="form-group mb-4">
                  <label className="form-label">Select Existing</label>
                  <div className="d-flex gap-2">
                    <select
                      value={selectedConfigObj}
                      onChange={(e) => setSelectedConfigObj(e.target.value)}
                      className="premium-input"
                    >
                      <option value="">-- Select a SuperAppObject --</option>
                      {configObjList.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                    {selectedConfigObj && (
                      <button
                        onClick={deleteConfigObj}
                        className="icon-btn delete-btn"
                        title="Delete Object"
                        style={{
                          padding: "10px",
                          background: "rgba(255, 107, 107, 0.1)",
                          border: "1px solid rgba(255, 107, 107, 0.2)",
                        }}
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="sub-card">
                      <h4 className="sub-card-title">
                        <FaCopy className="icon-sm" /> Copy Existing
                      </h4>
                      <div className="form-group mb-3">
                        <label className="form-label">Source</label>
                        <select
                          value={selectedSourceConfigObj}
                          onChange={(e) =>
                            setSelectedSourceConfigObj(e.target.value)
                          }
                          className="premium-input"
                        >
                          <option value="">-- Select source --</option>
                          {configObjList.map((name) => (
                            <option key={name} value={name}>
                              {name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group mb-3">
                        <label className="form-label">New Name</label>
                        <input
                          type="text"
                          value={newConfigObjName}
                          onChange={(e) => setNewConfigObjName(e.target.value)}
                          placeholder="e.g., MyNewTable"
                          className="premium-input"
                        />
                      </div>
                      <button
                        onClick={copyConfigObj}
                        disabled={
                          saving ||
                          !selectedSourceConfigObj ||
                          !newConfigObjName
                        }
                        className="primary-gradient-btn w-100 justify-content-center"
                      >
                        {saving ? "Copying..." : "Copy Object"}
                      </button>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="sub-card h-100">
                      <h4 className="sub-card-title">
                        <FaPlus className="icon-sm" /> Create New
                      </h4>
                      <p className="text-muted mb-4">
                        Create a brand new empty SuperAppObject configuration.
                      </p>
                      <button
                        onClick={createNewConfigObj}
                        disabled={saving}
                        className="secondary-btn w-100 justify-content-center"
                      >
                        {saving ? "Creating..." : "Create New Object"}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Bulk Import Section */}
            {/* Vik (Jan 21, 2026) : Only show Bulk Import in "import" mode (developer only) to prevent confusion for standard users. */}
            {mode === "import" && (
              <div className="sub-card mt-4">
                <h4 className="sub-card-title">
                  <FaCode className="icon-sm" /> Bulk Import / Templates
                </h4>
                <p className="text-muted mb-3">
                  Import multiple SuperAppObjects at once using JSON or select a
                  template.
                </p>

                <div className="form-group mb-3">
                  <label className="form-label">Load Template</label>
                  <select
                    onChange={(e) => handleLoadTemplate(e.target.value)}
                    className="premium-input"
                    defaultValue=""
                  >
                    <option value="" disabled>
                      -- Select a Template --
                    </option>
                    <option value="users">Users Table</option>
                    <option value="products">Products Table</option>
                    <option value="tasks">Tasks Table</option>
                  </select>
                </div>

                <div className="form-group mb-3">
                  <label className="form-label">
                    JSON Configuration (Array of Objects)
                  </label>
                  <textarea
                    value={bulkJson}
                    onChange={(e) => setBulkJson(e.target.value)}
                    placeholder='[
  {
    "name": "MyObject",
    "fields": [...],
    "table": [...]
  }
]'
                    className="premium-input code-font"
                    rows={8}
                  />
                </div>

                <button
                  onClick={handleBulkImport}
                  disabled={saving || !bulkJson.trim()}
                  className="primary-gradient-btn w-100 justify-content-center"
                >
                  {saving ? "Importing..." : "Import Objects"}
                </button>
              </div>
            )}

            {selectedConfigObj && (
              <div className="info-box mt-4">
                <p className="mb-1">
                  <strong>Selected:</strong>{" "}
                  <span className="highlight">{selectedConfigObj}</span>
                </p>
                <p className="text-muted small mb-0">
                  Use the tabs above to edit fields, table columns, or view raw
                  JSON.
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === "fields" && selectedConfigObj && (
          <div className="animate-fade-in">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h3 className="section-title">
                Fields Editor:{" "}
                <span className="highlight">{selectedConfigObj}</span>
              </h3>
              <button
                onClick={saveFields}
                disabled={saving}
                className="primary-gradient-btn"
              >
                <FaSave /> {saving ? "Saving..." : "Save Fields"}
              </button>
            </div>

            {/* Fields List */}
            <div className="mb-4">
              <div className="table-responsive">
                <table className="premium-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Display Name</th>
                      <th>Type</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {configObjFields.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-4">
                          No fields yet. Add one below.
                        </td>
                      </tr>
                    ) : (
                      configObjFields.map((field, index) => (
                        <tr key={index}>
                          <td>{field.name}</td>
                          <td>{field.display_name}</td>
                          <td>
                            <span className="badge-type">
                              {field.input_type}
                            </span>
                          </td>
                          <td className="text-center">
                            <button
                              onClick={() => startEditField(index)}
                              className="icon-btn edit-btn"
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => deleteField(index)}
                              className="icon-btn delete-btn"
                              title="Delete"
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Add/Edit Field Form */}
            <div className="sub-card">
              <h4 className="sub-card-title">
                {editingFieldIndex !== null ? (
                  <>
                    <FaEdit className="icon-sm" /> Edit Field
                  </>
                ) : (
                  <>
                    <FaPlus className="icon-sm" /> Add New Field
                  </>
                )}
              </h4>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Name (field key)</label>
                  <input
                    type="text"
                    value={fieldForm.name}
                    onChange={(e) =>
                      setFieldForm({ ...fieldForm, name: e.target.value })
                    }
                    placeholder="e.g., userId"
                    className="premium-input"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Display Name</label>
                  <input
                    type="text"
                    value={fieldForm.display_name}
                    onChange={(e) =>
                      setFieldForm({
                        ...fieldForm,
                        display_name: e.target.value,
                      })
                    }
                    placeholder="e.g., User ID"
                    className="premium-input"
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Input Type</label>
                  <select
                    value={fieldForm.input_type}
                    onChange={(e) =>
                      setFieldForm({ ...fieldForm, input_type: e.target.value })
                    }
                    className="premium-input"
                  >
                    <option value="text">Text</option>
                    <option value="textarea">Textarea</option>
                    <option value="number">Number</option>
                    <option value="email">Email</option>
                    <option value="dropdown">Dropdown</option>
                    <option value="checkbox">Checkbox</option>
                    <option value="date">Date</option>
                    <option value="color">Color</option>
                    <option value="display">Display</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Placeholder</label>
                  <input
                    type="text"
                    value={fieldForm.placeholder}
                    onChange={(e) =>
                      setFieldForm({
                        ...fieldForm,
                        placeholder: e.target.value,
                      })
                    }
                    placeholder="e.g., Enter user ID"
                    className="premium-input"
                  />
                </div>
              </div>

              {fieldForm.input_type === "dropdown" && (
                <div className="mb-3">
                  <label className="form-label">Values (comma-separated)</label>
                  <input
                    type="text"
                    value={fieldForm.values}
                    onChange={(e) =>
                      setFieldForm({ ...fieldForm, values: e.target.value })
                    }
                    placeholder="Option 1, Option 2, Option 3"
                    className="premium-input"
                  />
                </div>
              )}

              <div className="d-flex gap-4 mb-4">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={fieldForm.required}
                    onChange={(e) =>
                      setFieldForm({ ...fieldForm, required: e.target.checked })
                    }
                  />
                  Required
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={fieldForm.readonly}
                    onChange={(e) =>
                      setFieldForm({ ...fieldForm, readonly: e.target.checked })
                    }
                  />
                  Read-only
                </label>
              </div>

              <div className="d-flex gap-2">
                {editingFieldIndex !== null ? (
                  <>
                    <button
                      onClick={updateField}
                      className="primary-gradient-btn"
                    >
                      Update Field
                    </button>
                    <button
                      onClick={() => {
                        setEditingFieldIndex(null);
                        resetFieldForm();
                      }}
                      className="secondary-btn"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button onClick={addField} className="primary-gradient-btn">
                    <FaPlus /> Add Field
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "table" && selectedConfigObj && (
          <div className="animate-fade-in">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h3 className="section-title">
                Table/Column Editor:{" "}
                <span className="highlight">{selectedConfigObj}</span>
              </h3>
              <button
                onClick={saveTableColumns}
                disabled={saving}
                className="primary-gradient-btn"
              >
                <FaSave /> {saving ? "Saving..." : "Save Columns"}
              </button>
            </div>

            {/* Columns List */}
            <div className="mb-4">
              <div className="table-responsive">
                <table className="premium-table">
                  <thead>
                    <tr>
                      <th>Selector</th>
                      <th>Name</th>
                      <th>Sortable</th>
                      <th>Width</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableColumns.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-4">
                          No columns yet. Add one below.
                        </td>
                      </tr>
                    ) : (
                      tableColumns.map((column, index) => (
                        <tr key={index}>
                          <td className="font-monospace small">
                            {column.selector || ""}
                          </td>
                          <td>{column.name}</td>
                          <td>{column.sortable ? "✓" : ""}</td>
                          <td>{column.minWidth || "25%"}</td>
                          <td className="text-center">
                            <button
                              onClick={() => startEditColumn(index)}
                              className="icon-btn edit-btn"
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => deleteColumn(index)}
                              className="icon-btn delete-btn"
                              title="Delete"
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Add/Edit Column Form */}
            <div className="sub-card">
              <h4 className="sub-card-title">
                {editingColumnIndex !== null ? (
                  <>
                    <FaEdit className="icon-sm" /> Edit Column
                  </>
                ) : (
                  <>
                    <FaPlus className="icon-sm" /> Add New Column
                  </>
                )}
              </h4>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Field Selector</label>
                  <input
                    type="text"
                    value={columnForm.selector}
                    onChange={(e) =>
                      setColumnForm({ ...columnForm, selector: e.target.value })
                    }
                    placeholder="e.g., userId"
                    className="premium-input"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Column Name</label>
                  <input
                    type="text"
                    value={columnForm.name}
                    onChange={(e) =>
                      setColumnForm({ ...columnForm, name: e.target.value })
                    }
                    placeholder="e.g., User"
                    className="premium-input"
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Min Width</label>
                  <input
                    type="text"
                    value={columnForm.minWidth}
                    onChange={(e) =>
                      setColumnForm({ ...columnForm, minWidth: e.target.value })
                    }
                    placeholder="e.g., 25% or 180px"
                    className="premium-input"
                  />
                </div>
                <div className="col-md-6 d-flex align-items-end">
                  <label className="checkbox-label mb-2">
                    <input
                      type="checkbox"
                      checked={columnForm.sortable}
                      onChange={(e) =>
                        setColumnForm({
                          ...columnForm,
                          sortable: e.target.checked,
                        })
                      }
                    />
                    Sortable
                  </label>
                </div>
              </div>

              <div className="d-flex gap-2">
                {editingColumnIndex !== null ? (
                  <>
                    <button
                      onClick={updateColumn}
                      className="primary-gradient-btn"
                    >
                      Update Column
                    </button>
                    <button
                      onClick={() => {
                        setEditingColumnIndex(null);
                        resetColumnForm();
                      }}
                      className="secondary-btn"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button onClick={addColumn} className="primary-gradient-btn">
                    <FaPlus /> Add Column
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === "json" && selectedConfigObj && (
          <div className="animate-fade-in">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h3 className="section-title">
                Raw JSON: <span className="highlight">{selectedConfigObj}</span>
              </h3>
              <button
                onClick={saveRawJson}
                disabled={saving}
                className="primary-gradient-btn"
              >
                <FaSave /> {saving ? "Saving..." : "Save JSON"}
              </button>
            </div>

            <textarea
              value={rawJson}
              onChange={(e) => setRawJson(e.target.value)}
              className="code-editor"
              placeholder='{"name": "...", "fields": [...], "table": [...]}'
            />
          </div>
        )}
      </div>

      <style>{`
        .unified-editor-wrapper {
          color: #fff;
          font-family: 'Inter', sans-serif;
        }
        
        /* Tabs */
        .editor-tabs {
          display: flex;
          gap: 4px;
          margin-bottom: 24px;
          border-bottom: 1px solid rgba(255,255,255,0.1);
          overflow-x: auto;
          padding-bottom: 1px;
        }
        .editor-tab {
          padding: 10px 20px;
          background: transparent;
          border: none;
          border-bottom: 2px solid transparent;
          color: rgba(255,255,255,0.6);
          cursor: pointer;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .editor-tab:hover {
          color: #fff;
          background: rgba(255,255,255,0.05);
        }
        .editor-tab.active {
          color: #fff;
          border-bottom-color: #764ba2;
          background: rgba(118, 75, 162, 0.1);
        }
        .tab-icon {
          font-size: 0.9em;
        }
        
        /* Layout */
        .tab-content-area {
          min-height: 400px;
        }
        .section-header {
          margin-bottom: 24px;
        }
        .section-title {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0;
          color: #fff;
        }
        .highlight {
          color: #764ba2;
        }
        
        /* Cards & Containers */
        .sub-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.05);
          border-radius: 12px;
          padding: 20px;
          height: 100%;
        }
        .sub-card-title {
          font-size: 1rem;
          font-weight: 600;
          margin-top: 0;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          color: rgba(255,255,255,0.9);
        }
        .info-box {
          background: rgba(118, 75, 162, 0.1);
          border: 1px solid rgba(118, 75, 162, 0.2);
          border-radius: 8px;
          padding: 16px;
        }
        
        /* Forms */
        .form-group {
          margin-bottom: 16px;
        }
        .form-label {
          display: block;
          margin-bottom: 8px;
          font-size: 0.85rem;
          font-weight: 500;
          color: rgba(255,255,255,0.7);
        }
        .premium-input {
          width: 100%;
          padding: 10px 14px;
          background: rgba(0,0,0,0.2);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 6px;
          color: #fff;
          font-size: 14px;
          transition: all 0.2s;
        }
        .premium-input:focus {
          outline: none;
          border-color: #764ba2;
          background: rgba(0,0,0,0.3);
          box-shadow: 0 0 0 3px rgba(118, 75, 162, 0.1);
        }
        .premium-input option {
          background: #1a2035;
          color: #fff;
        }
        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 14px;
          color: rgba(255,255,255,0.9);
        }
        .code-editor {
          width: 100%;
          min-height: 400px;
          padding: 16px;
          background: #090b10;
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px;
          color: #a9b7c6;
          font-family: 'JetBrains Mono', monospace;
          font-size: 13px;
          line-height: 1.5;
        }
        
        /* Tables */
        .table-responsive {
          overflow-x: auto;
          border: 1px solid rgba(255,255,255,0.05);
          border-radius: 8px;
        }
        .premium-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }
        .premium-table th {
          background: rgba(255,255,255,0.05);
          padding: 12px 16px;
          text-align: left;
          font-weight: 600;
          color: rgba(255,255,255,0.8);
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }
        .premium-table td {
          padding: 12px 16px;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          color: rgba(255,255,255,0.9);
        }
        .premium-table tr:last-child td {
          border-bottom: none;
        }
        .premium-table tr:hover td {
          background: rgba(255,255,255,0.02);
        }
        
        /* Buttons */
        .primary-gradient-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 8px 20px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
        }
        .primary-gradient-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(118, 75, 162, 0.3);
        }
        .primary-gradient-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          filter: grayscale(1);
        }
        .secondary-btn {
          background: rgba(255,255,255,0.1);
          color: #fff;
          border: 1px solid rgba(255,255,255,0.1);
          padding: 8px 20px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
        }
        .secondary-btn:hover {
          background: rgba(255,255,255,0.15);
        }
        .icon-btn {
          background: transparent;
          border: none;
          padding: 6px;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .edit-btn { color: #667eea; }
        .edit-btn:hover { background: rgba(102, 126, 234, 0.1); }
        .delete-btn { color: #ff6b6b; }
        .delete-btn:hover { background: rgba(255, 107, 107, 0.1); }
        
        /* Utils */
        .d-flex { display: flex; }
        .justify-content-between { justify-content: space-between; }
        .justify-content-center { justify-content: center; }
        .align-items-center { align-items: center; }
        .align-items-end { align-items: flex-end; }
        .gap-2 { gap: 8px; }
        .gap-4 { gap: 16px; }
        .mb-1 { margin-bottom: 4px; }
        .mb-2 { margin-bottom: 8px; }
        .mb-3 { margin-bottom: 16px; }
        .mb-4 { margin-bottom: 24px; }
        .mt-4 { margin-top: 24px; }
        .py-4 { padding-top: 24px; padding-bottom: 24px; }
        .w-100 { width: 100%; }
        .h-100 { height: 100%; }
        .text-center { text-align: center; }
        .text-muted { color: rgba(255,255,255,0.5); }
        .small { font-size: 0.85rem; }
        .row { display: flex; flex-wrap: wrap; margin: 0 -10px; }
        .col-md-6 { width: 50%; padding: 0 10px; box-sizing: border-box; }
        .icon-sm { font-size: 0.9em; }
        .badge-type {
          background: rgba(255,255,255,0.1);
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.85em;
          font-family: monospace;
        }
        .font-monospace { font-family: monospace; }
        
        /* Animations */
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
